import React from 'react';
import { useNavigate } from 'react-router-dom';
import MoreInfoPill from './components/MoreInfoPill';
import { glassSurface } from './config/glassTheme';
import './GamingPremiumCards.css';

const SHOP_TILES = [
  {
    number: '01',
    title: 'Build a permanent collection',
    copy: 'Create a collection that stays with your account. The virtual products and assets you obtain remain yours to revisit, organise and enjoy.',
  },
  {
    number: '02',
    title: 'Collect across real-life categories',
    copy: 'Acquire cars, property, jewellery, technology, fashion, sports gear, home and garden equipment, lifestyle products and much more.',
  },
  {
    number: '03',
    title: 'Grow it with Shop Credits',
    copy: 'Use Shop Credits to obtain the products you want, explore new categories and keep growing your collection over time. What you obtain stays with you.',
  },
];

const RPG_WORLDS = [
  {
    key: 'cybertech',
    eyebrow: 'Persistent science-fiction RPG',
    title: 'CYBERTECH',
    playerModel: 'One player. One persistent character.',
    description: 'Shape one life through professions, gems, cybernetics, equipment, medical systems, survival, engineering, salvage, vehicles, corporations, investigation and lasting consequences.',
    systems: '700+ interconnected game systems hold state, apply rules, preserve knowledge and outcomes, and keep the experience flowing around your character.',
    chips: ['Cybernetics and equipment', 'Engineering and salvage', 'Vehicles', 'Command Carrier progression', 'Investigation and factions', 'World persistence'],
    route: '/dashboard/rpg/cybertech',
    button: 'Explore CyberTech',
    image: '/gaming/cybertech-world.png',
    accent: '#63d5ef',
    accent2: '#114b68',
    bright: '#9aeaff',
  },
  {
    key: 'veridian',
    eyebrow: 'Persistent fantasy RPG',
    title: 'VERIDIAN',
    playerModel: 'One player. Up to four persistent characters.',
    description: 'Build a lasting party and live inside a fantasy world shaped by character development, professions, exploration, settlements, ownership, relationships, factions, discoveries and consequences.',
    systems: '700+ interconnected game systems keep information, apply rules, preserve outcomes and move one continuous adventure forward.',
    chips: ['Character development', 'Professions', 'Exploration', 'Settlements and property', 'Relationships and factions', 'World persistence'],
    route: '/dashboard/rpg/veridian',
    button: 'Explore Veridian',
    image: '/gaming/veridian-world.png',
    accent: '#d7a44a',
    accent2: '#6f4317',
    bright: '#f3c86f',
  },
];

function ProductButton({ children, onClick, accent2, accent, bright, className = '' }) {
  return (
    <button
      type="button"
      className={`gm-premium-button ${className}`}
      style={glassSurface(accent2, accent, bright)}
      onClick={onClick}
    >
      <span>{children}</span>
      <span aria-hidden="true" className="gm-premium-arrow">{'→'}</span>
    </button>
  );
}

function VirtualShopCard() {
  const navigate = useNavigate();
  return (
    <article className="gm-premium-card gm-shop-card gm-reveal gm-reveal-delay-1">
      <div
        className="gm-shop-image"
        style={{ backgroundImage: "linear-gradient(180deg, rgba(7, 10, 16, 0.02) 45%, rgba(7, 10, 16, 0.76) 100%), url('/gaming/virtual-shop-gallery.png')" }}
        role="img"
        aria-label="Virtual products across vehicles, property, jewellery, home, garden, sport and technology"
      />
      <div className="gm-shop-content">
        <div className="gm-premium-eyebrow gm-shop-eyebrow"><span />Premium virtual collection experience</div>
        <div className="gm-shop-heading-row">
          <div>
            <h2>VIRTUAL SHOP</h2>
            <p className="gm-shop-intro">A premium virtual collection experience inside VISINVOKE Games. Acquire products and assets across the full range of life, then build a collection that remains yours and grows with you.</p>
          </div>
          <ProductButton
            accent2="#43208a"
            accent="#8a66ff"
            bright="#c3b2ff"
            className="gm-shop-button"
            onClick={() => navigate('/dashboard/virtual-shop')}
          >
            Open Virtual Shop
          </ProductButton>
        </div>
        <div className="gm-shop-more-info">
          <MoreInfoPill productKey="virtual-shop" accent="#a98bff" />
        </div>
        <div className="gm-shop-tiles">
          {SHOP_TILES.map(tile => (
            <section key={tile.number} className="gm-shop-tile">
              <span className="gm-shop-number">{tile.number}</span>
              <h3>{tile.title}</h3>
              <p>{tile.copy}</p>
            </section>
          ))}
        </div>
      </div>
    </article>
  );
}

function RpgCard({ world, delay }) {
  const navigate = useNavigate();
  return (
    <article className={`gm-premium-card gm-rpg-card gm-reveal ${delay}`} style={{ '--gm-accent': world.accent }}>
      <div className="gm-rpg-image" style={{ backgroundImage: `linear-gradient(180deg, transparent 52%, rgba(7,10,15,0.9) 100%), url(${world.image})` }}>
        <span className="gm-rpg-player-model">{world.playerModel}</span>
      </div>
      <div className="gm-rpg-content">
        <div className="gm-premium-eyebrow"><span />{world.eyebrow}</div>
        <h2>{world.title}</h2>
        <p className="gm-rpg-description">{world.description}</p>
        <div className="gm-rpg-rule">
          <strong>Real game systems with AI narration.</strong>
          <span>The game determines what happened. AI narrates what happened.</span>
        </div>
        <div className="gm-rpg-systems">
          <span className="gm-rpg-system-count">700+</span>
          <p>{world.systems}</p>
        </div>
        <div className="gm-rpg-chips">
          {world.chips.map(chip => <span key={chip}>{chip}</span>)}
        </div>
        <ProductButton
          accent2={world.accent2}
          accent={world.accent}
          bright={world.bright}
          onClick={() => navigate(world.route)}
        >
          {world.button}
        </ProductButton>
      </div>
    </article>
  );
}

export default function GamingPremiumCards() {
  return (
    <>
      <VirtualShopCard />
      <div className="gm-rpg-grid">
        <RpgCard world={RPG_WORLDS[0]} delay="gm-reveal-delay-2" />
        <RpgCard world={RPG_WORLDS[1]} delay="gm-reveal-delay-3" />
      </div>
    </>
  );
}
